/**
 * ActivityFormController — modale d'ajout / édition d'une activité
 * (EPIC-006 / 6.4).
 *
 * Pilote la modale `<dialog id="modal-activity-form">` :
 *   - Action `openAdd` : ouvre la modale en mode création.
 *   - Action `openEdit` : ouvre la modale en mode édition (id depuis dataset).
 *   - Action `close` : ferme la modale.
 *   - Action `regulatedChange` : affiche / masque les fieldsets RC Pro et
 *     décennale selon la catégorie réglementée sélectionnée.
 *   - Action `submit` : POST /activities (création) ou
 *     PATCH /activities/{id} (édition).
 *
 * Validation côté client :
 *   - `nafCode` est validé par `pattern="[0-9]{2}\.[0-9]{2}[A-Z]"` (HTML5).
 *   - Le serveur reste l'autorité (re-valide via Activity::validateNafCode et
 *     renvoie `invalid_naf_format` ou `validation_failed` en 422). Les codes
 *     d'erreur sont mappés en messages FR (vocabulaire « code APE »).
 *
 * Conventions :
 *   - Pas d'HTML inline en JS — la modale et tous ses champs sont dans le
 *     HTML statique (frontend/src/pages/profil-entreprise.html).
 *   - Au succès, dispatche un événement `activity-form:saved` capté par
 *     `activities-list` qui rafraîchit la liste.
 *   - L'édition ne pré-remplit pas par re-fetch — elle lit directement les
 *     données du DOM (cards déjà rendues par activities-list). KISS.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const ERROR_MESSAGES = {
  invalid_naf_format: "Le code APE est invalide. Format attendu : NN.NNL (ex. 62.01Z).",
  validation_failed: "Certains champs sont invalides. Vérifiez votre saisie.",
  activity_archived: "Cette activité est archivée et ne peut plus être modifiée.",
  activity_not_found: "Cette activité n'existe pas ou a été supprimée.",
  invalid_request: "Requête invalide.",
  unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

export default class ActivityFormController extends BaseController {
  static targets = [
    "modal",
    "form",
    "modalTitle",
    "submitBtn",
    "idInput",
    "immutableNotice",
    "categoryInput",
    "categoryHint",
    "nafInput",
    "nafError",
    "descriptionInput",
    "startDateInput",
    "startDateHint",
    "locationInput",
    "regulatedInput",
    "artisanalInput",
    "rcProBlock",
    "rcProInsurerInput",
    "rcProNumberInput",
    "rcProExpInput",
    "decennaleBlock",
    "decennaleInsurerInput",
    "decennaleNumberInput",
    "decennaleExpInput",
    "formError",
  ];

  // null = mode création ; uuid = mode édition.
  _editingId = null;

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  openAdd() {
    this._editingId = null;
    this._resetForm();

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = "Ajouter une activité";
    }
    if (this.hasCategoryInputTarget) {
      // En création, la catégorie est libre.
      this.categoryInputTarget.disabled = false;
    }
    if (this.hasCategoryHintTarget) {
      this.categoryHintTarget.textContent =
        "Choisissez la catégorie d'imposition (BIC vente, BIC services ou BNC).";
    }
    if (this.hasStartDateInputTarget) {
      this.startDateInputTarget.disabled = false;
    }
    if (this.hasStartDateHintTarget) {
      this.startDateHintTarget.textContent = "";
    }
    if (this.hasImmutableNoticeTarget) {
      this.immutableNoticeTarget.hidden = false;
    }

    this._updateConditionalFields();
    this._openModal();
  }

  openEdit(event) {
    const btn = event.currentTarget;
    const id = btn?.dataset?.id;
    if (!id) return;

    // Lire les données depuis la card (DOM) — KISS, évite un re-fetch.
    const card = btn.closest("[data-activity-id]");
    if (!card) return;

    this._editingId = id;
    this._resetForm();

    if (this.hasModalTitleTarget) {
      this.modalTitleTarget.textContent = "Modifier l'activité";
    }
    if (this.hasIdInputTarget) {
      this.idInputTarget.value = id;
    }

    // Pour le pré-remplissage, on s'appuie sur l'événement `activities-list:edit`
    // que le controller activities-list dispatchera avec l'objet activity complet.
    // Ici on utilise une approche directe : retrouver l'objet via le cache
    // exposé sur l'élément du controller activities-list.
    const activity = this._findActivityById(id);
    if (activity) {
      this._fillFormFromActivity(activity);
    }

    if (this.hasCategoryInputTarget) {
      // En édition, la catégorie est non-modifiable (D7 EPIC-006 : le change-
      // ment de catégorie passe par un parcours dédié, hors V1).
      this.categoryInputTarget.disabled = true;
    }
    if (this.hasCategoryHintTarget) {
      this.categoryHintTarget.textContent =
        "Non modifiable après création (un changement de catégorie nécessite un parcours dédié).";
    }
    if (this.hasStartDateInputTarget) {
      // start_date est dérivée système (spec §02e §17) : date de début ME
      // pour la principale, date de dépôt de la formalité INPI initiale
      // pour les secondaires. Non modifiable a posteriori.
      this.startDateInputTarget.disabled = true;
    }
    if (this.hasStartDateHintTarget) {
      this.startDateHintTarget.textContent =
        "Non modifiable (date fixée par la formalité INPI initiale).";
    }
    if (this.hasImmutableNoticeTarget) {
      this.immutableNoticeTarget.hidden = true;
    }

    this._updateConditionalFields();
    this._openModal();
  }

  close() {
    if (!this.hasModalTarget) return;
    if (typeof this.modalTarget.close === "function") {
      this.modalTarget.close();
    } else {
      this.modalTarget.removeAttribute("open");
    }
  }

  _openModal() {
    if (!this.hasModalTarget) return;
    if (typeof this.modalTarget.showModal === "function") {
      this.modalTarget.showModal();
    } else {
      this.modalTarget.setAttribute("open", "");
    }
  }

  // ---------------------------------------------------------------------------
  // Champs conditionnels (RC Pro / décennale)
  // ---------------------------------------------------------------------------

  regulatedChange() {
    this._updateConditionalFields();
  }

  _updateConditionalFields() {
    const regValue = this.hasRegulatedInputTarget ? this.regulatedInputTarget.value : "";

    if (this.hasRcProBlockTarget) {
      // RC Pro affichée pour toute catégorie réglementée non vide.
      this.rcProBlockTarget.hidden = !regValue;
    }
    if (this.hasDecennaleBlockTarget) {
      // Décennale uniquement pour bâtiment.
      this.decennaleBlockTarget.hidden = regValue !== "batiment";
    }
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    if (event) event.preventDefault();
    if (!this.hasFormTarget) return;

    // Validation HTML5 native (pattern, required) avant envoi.
    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity();
      return;
    }

    this._clearErrors();
    this._setSubmitDisabled(true);

    const payload = this._buildPayload();

    try {
      let response;
      if (this._editingId) {
        response = await api.updateActivity(this._editingId, payload);
      } else {
        response = await api.createActivity(payload);
      }

      this.close();
      this.showSuccess(
        this._editingId
          ? "Activité modifiée."
          : "Activité ajoutée. Une formalité INPI a été préparée.",
      );

      // Notifier activities-list de rafraîchir.
      this.element.dispatchEvent(
        new CustomEvent("activity-form:saved", { bubbles: true, detail: response }),
      );
    } catch (err) {
      this._renderError(err);
    } finally {
      this._setSubmitDisabled(false);
    }
  }

  _buildPayload() {
    const fd = new FormData(this.formTarget);
    const get = (name) => {
      const v = fd.get(name);
      return v === null ? null : (typeof v === "string" ? v.trim() : v);
    };
    const orNull = (v) => (v === "" || v === null || v === undefined ? null : v);

    const payload = {
      nafCode: get("nafCode") || "",
      description: get("description") || "",
      location: orNull(get("location")),
      regulatedCategory: orNull(get("regulatedCategory")),
      isArtisanal: fd.get("isArtisanal") === "on",
      rcProNumber: orNull(get("rcProNumber")),
      rcProInsurer: orNull(get("rcProInsurer")),
      rcProExpirationDate: orNull(get("rcProExpirationDate")),
      decennaleNumber: orNull(get("decennaleNumber")),
      decennaleInsurer: orNull(get("decennaleInsurer")),
      decennaleExpirationDate: orNull(get("decennaleExpirationDate")),
    };

    // En création seulement, on inclut `category` et `startDate`. En édition,
    // la catégorie (D7 EPIC-006) et la date de début (spec §02e §17 — dérivée
    // système) ne sont pas modifiables.
    if (!this._editingId) {
      payload.category = get("category") || "";
      payload.startDate = get("startDate") || "";
    }

    return payload;
  }

  // ---------------------------------------------------------------------------
  // Reset / hydrate
  // ---------------------------------------------------------------------------

  _resetForm() {
    if (this.hasFormTarget) this.formTarget.reset();
    if (this.hasIdInputTarget) this.idInputTarget.value = "";
    if (this.hasNafInputTarget) {
      this.nafInputTarget.setAttribute("aria-invalid", "false");
    }
    this._clearErrors();
  }

  _findActivityById(id) {
    // L'instance ActivitiesListController stocke `_activities` sur l'élément
    // racine. Comme les deux controllers partagent le même <body>, on remonte
    // jusqu'à l'élément qui porte les deux et on lit la propriété exposée.
    // KISS : on fait un fallback DOM (lecture des datasets de la card).
    const root = document.querySelector("[data-controller~='activities-list']");
    if (root && root.__activities) {
      return (root.__activities || []).find((a) => a.id === id) || null;
    }
    return null;
  }

  _fillFormFromActivity(activity) {
    if (this.hasCategoryInputTarget) this.categoryInputTarget.value = activity.category || "";
    if (this.hasNafInputTarget) this.nafInputTarget.value = activity.nafCode || "";
    if (this.hasDescriptionInputTarget) this.descriptionInputTarget.value = activity.description || "";
    if (this.hasStartDateInputTarget) {
      // Convertir ISO complet en YYYY-MM-DD si nécessaire.
      const sd = activity.startDate || "";
      this.startDateInputTarget.value = sd.length >= 10 ? sd.slice(0, 10) : sd;
    }
    if (this.hasLocationInputTarget) this.locationInputTarget.value = activity.location || "";
    if (this.hasRegulatedInputTarget) this.regulatedInputTarget.value = activity.regulatedCategory || "";
    if (this.hasArtisanalInputTarget) this.artisanalInputTarget.checked = !!activity.isArtisanal;

    if (this.hasRcProInsurerInputTarget) this.rcProInsurerInputTarget.value = activity.rcProInsurer || "";
    if (this.hasRcProNumberInputTarget) this.rcProNumberInputTarget.value = activity.rcProNumber || "";
    if (this.hasRcProExpInputTarget) {
      const v = activity.rcProExpirationDate || "";
      this.rcProExpInputTarget.value = v.length >= 10 ? v.slice(0, 10) : v;
    }

    if (this.hasDecennaleInsurerInputTarget) this.decennaleInsurerInputTarget.value = activity.decennaleInsurer || "";
    if (this.hasDecennaleNumberInputTarget) this.decennaleNumberInputTarget.value = activity.decennaleNumber || "";
    if (this.hasDecennaleExpInputTarget) {
      const v = activity.decennaleExpirationDate || "";
      this.decennaleExpInputTarget.value = v.length >= 10 ? v.slice(0, 10) : v;
    }
  }

  // ---------------------------------------------------------------------------
  // Erreurs
  // ---------------------------------------------------------------------------

  _clearErrors() {
    if (this.hasNafErrorTarget) {
      this.nafErrorTarget.textContent = "";
      this.nafErrorTarget.hidden = true;
    }
    if (this.hasFormErrorTarget) {
      this.formErrorTarget.textContent = "";
      this.formErrorTarget.hidden = true;
    }
    if (this.hasNafInputTarget) {
      this.nafInputTarget.setAttribute("aria-invalid", "false");
    }
  }

  _renderError(err) {
    const code = err?.data?.error?.code || err?.error?.code;
    const serverMsg = err?.data?.error?.message || err?.message || null;
    const friendly = (code && ERROR_MESSAGES[code]) || serverMsg || "Erreur lors de l'enregistrement.";

    if (code === "invalid_naf_format" && this.hasNafErrorTarget) {
      this.nafErrorTarget.textContent = friendly;
      this.nafErrorTarget.hidden = false;
      if (this.hasNafInputTarget) {
        this.nafInputTarget.setAttribute("aria-invalid", "true");
        this.nafInputTarget.focus();
      }
      return;
    }

    if (this.hasFormErrorTarget) {
      this.formErrorTarget.textContent = friendly;
      this.formErrorTarget.hidden = false;
    } else {
      this.showError(friendly);
    }
  }

  _setSubmitDisabled(disabled) {
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = disabled;
    }
  }
}
